import styles from "./Gauge.module.css";

interface GaugeProps {
  label: string;
  /** Current usage. `null` means unmetered (e.g. the relay link is off). */
  used: number | null;
  limit: number;
  unit?: string;
}

/**
 * Linear usage meter: used / limit with a track that fills toward the quota and
 * shifts colour as it nears (accent → warning ≥80% → danger at/over limit). A
 * `null` `used` renders an unmetered "—" with a neutral track.
 */
export function Gauge({ label, used, limit, unit }: GaugeProps) {
  const metered = used !== null;
  // role="meter" requires a real aria-valuenow; only use it when we actually have
  // a value AND a meaningful range. Unmetered / zero-limit falls back to a
  // presentational track (the visible "—" carries the meaning).
  const showMeter = used !== null && limit > 0;
  const ratio = metered && limit > 0 ? Math.min(used / limit, 1) : 0;
  const atLimit = metered && used >= limit;
  const near = metered && !atLimit && ratio >= 0.8;
  const tone = atLimit ? styles.danger : near ? styles.warn : styles.ok;
  const remaining = metered ? Math.max(limit - used, 0) : null;

  return (
    <div className={styles.gauge}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        {metered && (
          <span className={styles.remaining}>
            {atLimit ? "At limit" : `${remaining} left`}
          </span>
        )}
      </div>

      <div className={styles.value}>
        <span className={styles.used}>{metered ? used : "—"}</span>
        <span className={styles.limit}>
          / {limit}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>

      <div
        className={styles.track}
        role={showMeter ? "meter" : "presentation"}
        aria-label={showMeter ? label : `${label}: unmetered`}
        aria-valuenow={showMeter ? used : undefined}
        aria-valuemin={showMeter ? 0 : undefined}
        aria-valuemax={showMeter ? limit : undefined}
      >
        <div className={[styles.fill, tone].join(" ")} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}
